import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  fetchAvailablePartnerships,
  fetchPartnerships,
  fetchStaffPartnerships,
  createPartnershipThunk,
  deletePartnershipThunk,
} from './partnershipsThunks';

const initialState = {
  partnerships: [],
  availablePartnerships: [],
  totalCount: 0,
  loading: false,
  error: null,
  filters: {
    page: 1,
    rowsPerPage: 20,
    search: '',
    orderBy: 'name',
    order: 'asc'
  }
};

const partnershipsSlice = createSlice({
  name: 'partnerships',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.filters.rowsPerPage = action.payload;
      state.filters.page = 1; // reset page when rows per page changes
    },
    setSearch: (state, action) => {
      state.filters.search = action.payload;
      state.filters.page = 1; // reset page when search changes
    },
    setSorting: (state, action) => {
      state.filters.orderBy = action.payload.orderBy;
      state.filters.order = action.payload.order;
      state.filters.page = 1; // reset page when sorting changes
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Available
      .addCase(fetchAvailablePartnerships.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAvailablePartnerships.fulfilled, (state, action) => {
        state.availablePartnerships = action.payload;
      })
      .addCase(fetchAvailablePartnerships.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Fetch All
      .addCase(fetchPartnerships.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartnerships.fulfilled, (state, action) => {
        state.loading = false;
        state.partnerships = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchPartnerships.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // The same table read as the internal team, into the same fields.
      .addCase(fetchStaffPartnerships.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffPartnerships.fulfilled, (state, action) => {
        state.loading = false;
        state.partnerships = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchStaffPartnerships.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createPartnershipThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPartnershipThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPartnershipThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deletePartnershipThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePartnershipThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deletePartnershipThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, setPage, setRowsPerPage, setSearch, setSorting } = partnershipsSlice.actions;

// Persistir solo los filtros
const persistConfig = {
  key: 'partnerships',
  storage,
  whitelist: ['filters'],
};

export default persistReducer(persistConfig, partnershipsSlice.reducer);
